/**
 * Service layer — AlderFLOW's daily Focus walk (docs/ALDERFLOW.md §5c). Talks to /api/alderflow for the
 * walk itself, and CARRIES OUT a committed walk through the app's own services, so every launch check,
 * kit check and Core lock applies exactly as it does anywhere else (Matt, 2026-10-21: "Commit acts").
 */
import { createFlight } from './flights.js'
import { postMilestone } from './milestones.js'
import { pullOnDeck } from './onDeck.js'
import { saveRecordEdit } from './timeRecords.js'
import { getPassengersFor, movePassenger } from './passengers.js'
import { closeThread } from './threads.js'
import { getMilestoneSheetRevisions } from './sheets.js'
import { getRevisionQcSummaries } from './qcVersions.js'
import { isSheetGateMilestone } from '../journey/eligibility.js'
import { getMilestoneChecklistItems } from './checklist.js'
import { HIDDEN_CL_CATEGORIES } from '../outline/constants.js'
import { setGateItemStatus } from './gates.js'
import { scaffoldPhaseMilestones } from './wbs.js'
import { refreshMilestoneMirror } from './schedule.js'
import { createKeyDate, updateKeyDate } from './keyDates.js'
import { getQcAgreements, answerQcAgreement } from './qcVersions.js'

async function authToken() {
  if (typeof window === 'undefined') return null
  for (let i = 0; i < 20; i++) {
    if (typeof window.__clerkGetToken === 'function') {
      try { const t = await window.__clerkGetToken(); if (t) return t } catch { /* not ready */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}
async function post(body) {
  const token = await authToken()
  const res = await fetch('/api/alderflow', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || ('Request failed: ' + res.status))
  return json
}

/** { gated, walk? } — the tower's morning lockout; false for everyone else, and once committed or snoozed. */
export function getFocusWalkGate(memberId, memberName) { return post({ action: 'gate', memberId, memberName }) }
/** { date, since, cards, handled, findings, status } */
export function getFocusWalk(memberId, memberName) { return post({ action: 'walk', memberId, memberName }) }
export function snoozeFocusWalk(memberId, memberName) { return post({ action: 'snooze', memberId, memberName }) }

const sleep = (ms) => new Promise(r => setTimeout(r, ms))
const today = () => new Date(Date.now() - 7 * 3600000).toISOString().slice(0, 10)

/** Launch + crew, then move the crew's hours onto the milestone. A refusal is a result, not a throw. */
async function crew(act, lines) {
  const members = (act.members || []).map(({ memberAirtableId, memberName, isCaptain, priority }) => ({ memberAirtableId, memberName, isCaptain, priority }))
  const r = await createFlight(act.milestoneId, members, 0.1)
  // The walk never waives anything (Matt, 2026-10-22: no circumventing "our own laws of physics").
  // An open kit is a refusal like any other; the walk offers the fix (line it up, ask) instead.
  if (r && r.kitRequired) { lines.push({ ok: false, t: `${act.milestoneName} did not launch: its full kit is not ready (a predecessor is not posted).` }); return false }
  if (r && r.refused) { lines.push({ ok: false, t: `${act.milestoneName} did not launch: ${r.refused.message || r.refused.title || 'a launch check refused it'}` }); return false }
  if (!r || !r.flight) { lines.push({ ok: false, t: `${act.milestoneName} did not launch.` }); return false }
  lines.push({ ok: true, t: `${r.flight.flight_number || act.milestoneName} launched.` })
  let moved = 0, failed = 0
  for (const id of act.recodeIds || []) {
    try { await saveRecordEdit(id, { entityType: 'Deliverable', entityId: act.milestoneId, entityName: act.milestoneName }); moved++ }
    catch { failed++ }
  }
  if (moved || failed) lines.push({ ok: !failed, t: `${moved} time entr${moved === 1 ? 'y' : 'ies'} moved onto it${failed ? `, ${failed} could not be moved` : ''}.` })
  return true
}

/**
 * RUN A RECIPE (docs/ALDERFLOW.md §0b): carry out a choice's moves in order, the ONE place a move is
 * done (api/_moves.js words them; this does them). Anything that would leave a half-done record stops
 * the choice before it starts: threads are settled first, then every post is checked by the Post
 * window's own rules, and only then does anything post. `$planned` is the first milestone a plan move
 * in the same choice created. Returns nothing; each step adds a line to `lines`.
 */
async function runMoves(act, lines, { memberId, memberName }) {
  const moves = act.moves || []
  let planned = null
  const crewed = []          // { t, members } launched by this choice: each person gets a morning note
  const parked = []          // { name, hours, waitsFor } hours moved onto a milestone that waits
  const target = (x) => x === '$planned' ? planned : x
  // 0. Anything that would stop a post (checklist, QC) is checked BEFORE a thread moves, so a choice that
  //    can't post changes nothing at all (2026-10-25: Patch's threads moved though its post was refused).
  const toPost = moves.filter(x => x.kind === 'post').flatMap(x => x.milestones)
  for (const p of toPost) {
    const stop = await postingRefusal(p, { skipThreads: true })
    if (stop) { lines.push({ ok: false, t: stop + ' Nothing in this choice was carried out.' }); return }
  }
  // 1. threads aboard: moved on, or closed as handled
  for (const m of moves.filter(x => x.kind === 'riders')) {
    for (const r of m.list) {
      try {
        if (r.close) { await closeThread(r.threadId, memberId, 'Handled. Closed in the Operations Director Triage.'); lines.push({ ok: true, t: `${r.title}: closed as handled.` }) }
        else { await movePassenger(memberId, memberName, r.threadId, m.to.id, 'Moved in the Operations Director Triage'); lines.push({ ok: true, t: `${r.title}: moved to ${m.to.name}.` }) }
      } catch (e) { lines.push({ ok: false, t: `${r.title}: ${String(e && e.message || e)}` }) }
    }
    await sleep(1500)
  }
  // 2. every post must pass the Post window's checks before any posts
  const posts = moves.filter(x => x.kind === 'post').flatMap(x => x.milestones)
  for (const p of posts) {
    const stop = await postingRefusal(p)
    if (stop) { lines.push({ ok: false, t: stop + ' Nothing in this choice was carried out.' }); return }
  }
  for (const m of moves) {
    try {
      if (m.kind === 'post') {
        for (const p of m.milestones) { await postMilestone(p.id, { date: (m.dated && act.asOf) || today() }); lines.push({ ok: true, t: `${p.name} posted.` }) }
        await sleep(2500)   // the schedule mirror refreshes the post before the next launch checks it
      } else if (m.kind === 'plan') {
        const r = await scaffoldPhaseMilestones({ phaseRecordId: m.phaseId, anchorDate: m.anchorDate || null, templatePhase: m.template })
        if (r && r.error) throw new Error(r.error)
        if (!r || !r.created) { lines.push({ ok: false, t: `${m.phaseName}: ${r && r.reason ? r.reason : 'no milestones were created'}. Nothing else in this choice was carried out.` }); return }
        lines.push({ ok: true, t: `${r.created} milestone${r.created === 1 ? '' : 's'} planned in ${m.phaseName}.` })
        await refreshMilestoneMirror(r.milestoneIds); await sleep(1500)
        planned = { id: r.milestoneIds[0], name: m.first || 'The first milestone' }
      } else if (m.kind === 'crew') {
        const t = target(m.milestone)
        if (!t) { lines.push({ ok: false, t: 'There was no milestone to crew.' }); continue }
        const one = { milestoneId: t.id, milestoneName: t.name, members: m.members, recodeIds: [] }
        let ok = await crew(one, lines)
        if (!ok && posts.length) { await sleep(3000); lines.pop(); ok = await crew(one, lines) }
        if (!ok && m.milestone === '$planned') { await pullOnDeck(t.id, { memberId, memberName, note: 'From the Operations Director Triage' }); lines.push({ ok: true, t: `${t.name} is lined up instead.` }) }
        if (ok) crewed.push({ t, members: m.members || [] })
      } else if (m.kind === 'moveHours') {
        const t = target(m.to)
        if (!t || !(m.ids || []).length) continue
        let moved = 0, failed = 0
        for (const id of m.ids) { try { await saveRecordEdit(id, { entityType: 'Deliverable', entityId: t.id, entityName: t.name }); moved++ } catch { failed++ } }
        lines.push({ ok: !failed, t: `${moved} time entr${moved === 1 ? 'y' : 'ies'} moved onto ${t.name}${failed ? `, ${failed} could not be moved` : ''}.` })
        const waits = moves.find(x => x.kind === 'lineup' && Array.isArray(x.milestones) && x.milestones.some(y => y.id === t.id))
        if (moved && !crewed.some(c => c.t.id === t.id) && !moves.some(x => x.kind === 'crew' && x.milestone && x.milestone.id === t.id)) parked.push({ name: t.name, hours: m.hours, waitsFor: waits && waits.waitsFor })
      } else if (m.kind === 'lineup') {
        const list = m.milestones === '$planned' ? [planned].filter(Boolean) : m.milestones
        for (const x of list) await pullOnDeck(x.id, { memberId, memberName, note: 'From the Operations Director Triage' })
        lines.push({ ok: true, t: `${list.map(x => x.name).join(', ')} lined up.` })
      } else if (m.kind === 'ask') {
        const r = await post({ action: 'ask', memberId: m.to.id, alsoMemberIds: m.also || [], projectId: m.projectId || null, text: m.text, fromName: memberName, fromMemberId: memberId,
          instruction: m.post ? { kind: 'post', milestoneId: m.post.id, flightNumber: m.post.name, toName: m.to.name, text: m.instructionText || m.text, note: m.note || null } : null })
        lines.push({ ok: !!r.ok, t: r.ok ? `Asked ${String(m.to.name || '').split(' ')[0]} in Slack.` : `The Slack question to ${String(m.to.name || '').split(' ')[0]} did not go out (${r.error}).` })
      } else if (m.kind === 'keyDate') {
        if (!m.date || m.date === '$date') { lines.push({ ok: false, t: 'No date was entered.' }); continue }
        if (m.keyDateId) await updateKeyDate(m.keyDateId, { date: m.date, memberId, memberName })
        else await createKeyDate(m.projectId, { label: m.label, date: m.date, milestoneIds: m.milestoneId ? [m.milestoneId] : [], memberId, memberName })
        lines.push({ ok: true, t: `${m.label} saved: ${new Date(m.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.` })
      } else if (m.kind === 'resolveNote') {
        await post({ action: 'resolveNote', id: m.id, memberName })
        lines.push({ ok: true, t: 'Note handled.' })
      } else if (m.kind === 'target') {
        if (!m.date || m.date === '$date') { lines.push({ ok: false, t: 'No Build Ready date was entered.' }); continue }
        await setProjectTarget({ projectId: m.projectId, projectName: m.name, date: m.date, chainDays: m.chainDays, bufferDays: m.bufferDays, basis: m.basis, reason: m.reason || null, memberId, memberName })
        lines.push({ ok: true, t: `${m.name}’s Build Ready date is ${new Date(m.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.` })
      } else if (m.kind === 'later') {
        const r = await post({ action: 'cardLater', cardId: m.cardId, days: m.days, memberId, memberName })
        lines.push({ ok: !!(r && r.ok), t: r && r.ok ? `It asks again on ${new Date(r.until + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}.` : 'It couldn’t be put off.' })
      } else if (m.kind === 'qcAgree') {
        const a = await answerQcAgreement(m.agreementId, m.decision, { memberId, memberName })
        lines.push({ ok: true, t: m.decision === 'decline' ? 'You didn’t agree; the sheets go through QC.' : a && a.applied_at ? `Both directors agreed: ${(a.apply_result || []).filter(x => x.ok).length} sheet${(a.apply_result || []).filter(x => x.ok).length === 1 ? '' : 's'} are QC approved.` : 'Your agreement is recorded; it waits for the other director.' })
      } else if (m.kind === 'note') {
        const r = await post({ action: 'notes', notes: [{ toMemberId: m.to.id, toName: m.to.name, milestoneId: m.milestoneId || null, text: m.text }], fromName: memberName, fromMemberId: memberId })
        lines.push({ ok: !!(r && r.count), t: r && r.count ? `A note goes to ${String(m.to.name || '').split(' ')[0]} for tomorrow morning.` : 'The note didn’t save.' })
      } else if (m.kind === 'none') lines.push({ ok: true, t: 'No change.' })
    } catch (e) { lines.push({ ok: false, t: String(e && e.message || e) }) }
  }
  // MORNING NOTES (Matt, 2026-10-24): everyone moved hears, in their own triage tomorrow morning, what
  // they're on now, why, and what's waiting. Plain words; no Slack.
  if (crewed.length) {
    const notes = []
    for (const c of crewed) for (const p of c.members) {
      const cap = c.members.length > 1 ? c.members.find(x => x.isCaptain) || c.members[0] : null
      const capLine = cap ? (cap.memberAirtableId === p.memberAirtableId ? ', and you’re its captain' : `, with ${String(cap.memberName || '').split(' ')[0]} as captain`) : ''
      const park = parked.map(x => ` The hours you logged before were moved onto ${x.name}${x.waitsFor ? `, which waits until ${x.waitsFor} posts` : ''}.`).join('')
      notes.push({ toMemberId: p.memberAirtableId, toName: p.memberName, milestoneId: c.t.id, flightNumber: c.t.name,
        text: `${act.story ? act.story + ' ' : ''}You’re on ${c.t.name} now${capLine}.${park}` })
    }
    try { const r = await post({ action: 'notes', notes, fromName: memberName, fromMemberId: memberId }); if (r && r.count) lines.push({ ok: true, t: `${r.count === 1 ? 'A note goes' : `${r.count} notes go`} to ${crewed.flatMap(c => c.members).map(p => String(p.memberName || '').split(' ')[0]).join(' and ')} for tomorrow morning.` }) }
    catch (e) { lines.push({ ok: false, t: `The morning notes didn’t save (${String(e && e.message || e)}).` }) }
  }
}

/** Why a milestone can't post, by the Post window's own checks (PostMilestoneModal), or null. */
/** Everything the Post window would stop a post on, plus open checklist items (not a stop): the
 *  Operations Director Triage shows it before asking someone to post (Matt, 2026-10-22). */
export async function postHeadsUp(msId) {
  const out = []
  try {
    const revs = await getMilestoneSheetRevisions(msId)
    if (revs && revs.length) {
      const qc = await getRevisionQcSummaries(revs.map(x => x.id))
      const open = revs.filter(x => !(qc[x.id] && qc[x.id].approved)).length
      if (open) out.push(`${open} drawing sheet${open === 1 ? ' is' : 's are'} not yet cleared through QC; the post window won’t let it post until they are.`)
    }
  } catch { out.push('Its drawing sheets could not be checked.') }
  try {
    const open = await openChecklistCount(msId)
    if (open) out.push(`${open} checklist item${open === 1 ? ' is' : 's are'} still open; each has to be completed or marked not required before it can post.`)
  } catch { out.push('Its checklist could not be checked.') }
  return out
}
/** The Post window's checklist gate (PostMilestoneModal.jsx): every item completed or marked not
 *  required, QA/QC and the retired Full-Kit items aside. */
/**
 * WHAT STANDS BETWEEN A MILESTONE AND POSTING (2026-10-25: two posts were refused only at Commit):
 * its open checklist items (by name, so the card can settle them in place) and its sheets not yet
 * through QC (and any QC-by-agreement request already open for it).
 */
export async function postReadiness(msId) {
  const out = { id: msId, openItems: [], unQc: [], agreement: null }
  try {
    const items = await getMilestoneChecklistItems(msId)
    out.openItems = (items || []).filter(it => {
      const f = it.fields || {}
      const c = f['Category (from Deliverable Checklist Item Template)']
      const cat = (Array.isArray(c) ? c[0] : c) || 'Other'
      return f['Complete'] !== true && f['Milestone Not Required'] !== true && !HIDDEN_CL_CATEGORIES.includes(cat)
    }).map(it => { const n = it.fields['Name for List (from Deliverable Checklist Item Template)']; return { id: it.id, name: (Array.isArray(n) ? n[0] : n) || 'Checklist item' } })
  } catch { /* shown as unknown */ }
  try {
    const revs = await getMilestoneSheetRevisions(msId)
    if (revs && revs.length) {
      const qc = await getRevisionQcSummaries(revs.map(x => x.id))
      out.unQc = revs.filter(x => !(qc[x.id] && qc[x.id].approved)).map(x => {
        const f = x.fields || {}, sh = f['Drawing Sheet'], t = f['Sheet Number and Title (from Drawing Sheet)']
        return { revisionId: x.id, sheetId: Array.isArray(sh) ? sh[0] : sh, title: (Array.isArray(t) ? t[0] : t) || 'Sheet' }
      })
      if (out.unQc.length) { const open = await getQcAgreements(msId); out.agreement = open[0] || null }
    }
  } catch { /* shown as unknown */ }
  return out
}

async function openChecklistCount(msId) {
  const items = await getMilestoneChecklistItems(msId)
  return (items || []).filter(it => {
    const f = it.fields || {}
    const c = f['Category (from Deliverable Checklist Item Template)']
    const cat = (Array.isArray(c) ? c[0] : c) || 'Other'
    return f['Complete'] !== true && f['Milestone Not Required'] !== true && !HIDDEN_CL_CATEGORIES.includes(cat)
  }).length
}
async function postingRefusal(p, { skipThreads = false } = {}) {
  if (!skipThreads) try {
    const r = await getPassengersFor([p.id])
    const aboard = ((r && r.byMilestone) || {})[p.id] || []
    if (aboard.length) return `${p.name} can’t post: ${aboard.length} open thread${aboard.length === 1 ? ' rides' : 's ride'} on it.`
  } catch { return `${p.name}: could not check for threads aboard.` }
  try {
    const open = await openChecklistCount(p.id)
    if (open) return `${p.name} can’t post: ${open} checklist item${open === 1 ? ' is' : 's are'} still open (complete them or mark them not required).`
  } catch { return `${p.name}: could not check its checklist.` }
  try {
    const revs = await getMilestoneSheetRevisions(p.id)
    if (!revs || !revs.length) return isSheetGateMilestone(p.typeId) ? `${p.name} can’t post: drawing sheets must be added first.` : null
    const qc = await getRevisionQcSummaries(revs.map(x => x.id))
    const open = revs.filter(x => !(qc[x.id] && qc[x.id].approved)).length
    return open ? `${p.name} can’t post: ${open} drawing sheet${open === 1 ? '' : 's'} not yet cleared through QC.` : null
  } catch { return `${p.name}: could not check its drawing sheets.` }
}

/**
 * Carry out the committed decisions in order. Each returns plain result lines; one failure never
 * stops the rest. Then the walk is recorded (proposed vs chosen, for the engine to learn from).
 */
export async function commitFocusWalk({ memberId, memberName, decisions }) {
  const out = []
  for (const d of decisions) {
    const lines = []
    const act = d.act || { type: 'none' }
    try {
      // REVIEW THE KIT answers (2026-10-22) are recorded before anything launches.
      if (Array.isArray(act.kitAnswers) && act.kitAnswers.length) {
        let n = 0
        for (const k of act.kitAnswers) {
          try { await setGateItemStatus(k.id, k.status, { reason: k.reason || undefined, memberId, memberName }); n++ }
          catch (e) { lines.push({ ok: false, t: `Kit item "${k.name}": ${String(e && e.message || e)}` }) }
        }
        lines.push({ ok: true, t: `${n} kit item${n === 1 ? '' : 's'} recorded.` })
      }
      if (act.type === 'moves') await runMoves(act, lines, { memberId, memberName })
      else if (act.type === 'crew') await crew(act, lines)
      else if (act.type === 'postThenCrew') {
        const posts = act.posts || [{ id: act.postMilestoneId, name: act.postName }]
        // Threads aboard, reviewed on the card: closed as handled, or moved to the next milestone.
        for (const r of act.riders || []) {
          try {
            if (r.close) { await closeThread(r.threadId, memberId, 'Handled. Closed in the Operations Director Triage.'); lines.push({ ok: true, t: `${r.title}: closed as handled.` }) }
            else { await movePassenger(memberId, memberName, r.threadId, act.milestoneId, 'Moved in the Operations Director Triage'); lines.push({ ok: true, t: `${r.title}: moved to ${act.milestoneName}.` }) }
          } catch (e) { lines.push({ ok: false, t: `${r.title}: ${String(e && e.message || e)}` }) }
        }
        if ((act.riders || []).length) await sleep(1500)
        // The Post window's own checks, run before anything posts: no thread aboard, drawing sheets
        // QC-cleared. If any fails, nothing posts and nothing launches.
        let stop = null
        for (const p of posts) { stop = await postingRefusal(p); if (stop) break }
        if (stop) { lines.push({ ok: false, t: stop + ' Nothing was posted or launched.' }); out.push({ cardId: d.cardId, rule: d.rule, choice: d.choice, results: lines }); continue }
        for (const p of posts) {
          await postMilestone(p.id, { date: act.asOf || today() })
          lines.push({ ok: true, t: `${p.name} posted.` })
        }
        await sleep(2500)   // the schedule mirror refreshes the post before the next launch checks it
        // The hours so far go to a later milestone (worked ahead): the crew takes none of them.
        const crewAct = act.recodeTo ? { ...act, recodeIds: [] } : act
        const ok = await crew(crewAct, lines)
        if (!ok) { await sleep(3000); lines.pop(); await crew(crewAct, lines) }
        if (act.recodeTo) {
          let moved = 0, failed = 0
          for (const id of act.recodeIds || []) {
            try { await saveRecordEdit(id, { entityType: 'Deliverable', entityId: act.recodeTo, entityName: act.recodeToName }); moved++ } catch { failed++ }
          }
          lines.push({ ok: !failed, t: `${moved} time entr${moved === 1 ? 'y' : 'ies'} moved onto ${act.recodeToName}${failed ? `, ${failed} could not be moved` : ''}.` })
        }
        if (act.lineupAfter) {
          try { await pullOnDeck(act.lineupAfter, { memberId, memberName, note: 'Worked ahead; resumes after the milestone before it posts' }); lines.push({ ok: true, t: `${act.recodeToName || 'It'} is lined up.` }) }
          catch (e) { lines.push({ ok: false, t: String(e && e.message || e) }) }
        }
      } else if (act.type === 'ask') {
        const r = await post({ action: 'ask', memberId: act.memberId, alsoMemberIds: act.alsoMemberIds || [], projectId: act.projectId || null, text: act.text, fromName: memberName, fromMemberId: memberId,
          instruction: act.postMilestoneId ? { kind: 'post', milestoneId: act.postMilestoneId, flightNumber: act.postName, toName: act.memberName, text: act.instructionText || act.text, note: act.note || null } : null })
        lines.push({ ok: !!r.ok, t: r.ok ? `Asked ${String(act.memberName || '').split(' ')[0]} in Slack.` : `The Slack question to ${String(act.memberName || '').split(' ')[0]} did not go out (${r.error}).` })
      } else if (act.type === 'lineupMany') {
        let n = 0
        for (const id of act.milestoneIds || []) { try { await pullOnDeck(id, { memberId, memberName, note: 'From the Operations Director Triage' }); n++ } catch (e) { lines.push({ ok: false, t: String(e && e.message || e) }) } }
        lines.push({ ok: true, t: `${n} lined up.` })
      } else if (act.type === 'lineupAndAsk') {
        await pullOnDeck(act.milestoneId, { memberId, memberName, note: 'From the Operations Director Triage' })
        lines.push({ ok: true, t: 'Lined up.' })
        const r = await post({ action: 'ask', memberId: act.memberId, projectId: act.projectId || null, text: act.text, fromName: memberName })
        lines.push({ ok: !!r.ok, t: r.ok ? `Asked ${String(act.memberName || '').split(' ')[0]} in Slack.` : `The Slack question to ${String(act.memberName || '').split(' ')[0]} did not go out (${r.error}).` })
      } else if (act.type === 'planPhase') {
        // Plan the phase from its WBS template, then crew the first milestone with that phase's hours.
        const r = await scaffoldPhaseMilestones({ phaseRecordId: act.phaseId, anchorDate: act.anchorDate || null, templatePhase: act.templatePhase })
        if (r && r.error) throw new Error(r.error)
        if (!r || !r.created) { lines.push({ ok: false, t: `${act.phaseName}: ${r && r.reason ? r.reason : 'no milestones were created'}.` }); out.push({ cardId: d.cardId, rule: d.rule, choice: d.choice, results: lines }); continue }
        lines.push({ ok: true, t: `${r.created} milestone${r.created === 1 ? '' : 's'} planned in ${act.phaseName}.` })
        await refreshMilestoneMirror(r.milestoneIds)
        await sleep(1500)
        const firstId = r.milestoneIds[0]
        const crewAct = { ...act, type: 'crew', milestoneId: firstId, milestoneName: act.firstName || 'The first milestone' }
        const ok = await crew(crewAct, lines)
        if (!ok) { await pullOnDeck(firstId, { memberId, memberName, note: 'From the Operations Director Triage' }); lines.push({ ok: true, t: 'Lined up instead.' }) }
      } else if (act.type === 'lineup') {
        await pullOnDeck(act.milestoneId, { memberId, memberName, note: 'From the Operations Director Triage' })
        lines.push({ ok: true, t: 'Lined up.' })
      } else lines.push({ ok: true, t: 'No change.' })
    } catch (e) {
      lines.push({ ok: false, t: String(e && e.message || e) })
    }
    out.push({ cardId: d.cardId, rule: d.rule, choice: d.choice, results: lines, ...(d.answer ? { pre: d.pre || null, answer: d.answer } : null) })
  }
  await post({ action: 'record', memberId, memberName, decisions: out }).catch(() => {})
  return out
}

/** Instructions to me from the Operations Director Triage (a captain's morning report card). */
/** How often a question card's pick was right, per question, from committed walks. */
/** BUILD READY (docs/ALDERFLOW.md §5c2): every project's chain to Build Ready, its proposed target, its
 *  target and buffer burn. Resolves { today, projects }. */
export function getBuildReady() { return post({ action: 'buildReady' }) }
/** The where-the-work-goes board (projects with ready work by buffer burn; people on no milestone). */
export function getCrewBoard() { return post({ action: 'crewBoard' }) }
/** Crew what was picked on the board: each through the app's own launch (rope, kit, seats), refusals
 *  reported. picked = [{ ms: { id, name }, members: [{ id, name }] }]. Resolves [{ ok, t }]. */
export async function crewPicks(picked) {
  const lines = []
  for (const p of picked) {
    await crew({ milestoneId: p.ms.id, milestoneName: p.ms.name, recodeIds: [],
      members: p.members.map((m, i) => ({ memberAirtableId: m.id, memberName: m.name, isCaptain: i === 0, priority: 1 })) }, lines)
  }
  return lines
}
/** One project's picture for a card elsewhere (the progress update): its Build Ready row, what is in the
 *  air, what is ready to start, today's open triage questions about it, and notes waiting for the triage. */
export function getProjectBrief(projectId) { return post({ action: 'projectBrief', projectId }) }
/** Catch a thought for the next Operations Director Triage (database/346). */
export function addTriageNote({ projectId, projectName, milestoneId, milestoneName, text, memberId, memberName, source }) {
  return post({ action: 'addNote', projectId, projectName, milestoneId, milestoneName, text, memberId, memberName, source })
}
/** Set or move a project's Build Ready target (tower only; moving one needs a reason, kept in its history). */
export function setProjectTarget({ projectId, projectName, date, chainDays, bufferDays, basis, reason, memberId, memberName }) {
  return post({ action: 'setTarget', projectId, projectName, date, chainDays, bufferDays, basis, reason, memberId, memberName })
    .then(r => { try { window.dispatchEvent(new CustomEvent('build-ready-changed')) } catch (_) {} return r })
}
/** Set where a project's Build Ready plan starts (tower only; a reason is required, the old date is kept). */
export function setBaselineStart({ projectId, projectName, date, reason, memberName }) {
  return post({ action: 'setBaselineStart', projectId, projectName, date, reason, memberName })
    .then(r => { try { window.dispatchEvent(new CustomEvent('build-ready-changed')) } catch (_) {} return r })
}
export function getPickAccuracy(memberId) { return post({ action: 'accuracy', memberId }).catch(() => null) }
export function getMyInstructions(memberId) { return post({ action: 'myInstructions', memberId }).then(r => (r && r.instructions) || []) }
export function answerInstruction(memberId, memberName, id, answer, note) { return post({ action: 'answerInstruction', memberId, memberName, id, answer, note }) }

/** Save where this person is in today's Operations Director Triage (survives a reload or another computer). */
export function saveFocusWalkDraft(memberId, memberName, decisions, at) { return post({ action: 'saveDraft', memberId, memberName, decisions, at }) }
